import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Target, Handshake } from 'lucide-react';
import { siteData } from '../data/mock';

const AboutUs = () => {
  const { about } = siteData;

  const values = [
    {
      icon: Zap,
      title: "From Zero to Live in One Week",
      description: "Traditional agencies take months. We leverage AI to build your 'Digital Front Door' in just 7 days—3 days for the first draft, 3 days to refine, and you're ready to launch.",
    },
    {
      icon: Target,
      title: "Precision",
      description: "Every solution is tailored to your unique business needs—no cookie-cutter templates.",
    },
    {
      icon: Handshake,
      title: "Partnership",
      description: "We're invested in your success, providing ongoing support and strategic guidance.",
    },
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-[var(--bg-section)]">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] text-sm font-medium mb-4">
                About Us
              </span>
              <h2 className="heading-2 mb-6">
                {about.title}
              </h2>
              <p className="body-large mb-8">
                {about.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#services" className="btn-primary">
                  Explore Services
                </a>
                <a href="#contact" className="btn-secondary">
                  Talk to Us
                </a>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-2 gap-4"
            >
              {about.stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="card bg-white text-center py-8 group"
                >
                  <div className="text-4xl md:text-5xl font-bold text-[var(--accent-text)] mb-2 group-hover:scale-110 transition-transform duration-300">
                    {stat.value}
                  </div>
                  <div className="text-sm text-[var(--text-secondary)] font-medium">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Values */}
          <div className="mt-20 pt-16 border-t border-black/10">
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center md:text-left group"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] flex items-center justify-center mx-auto md:mx-0 mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="w-8 h-8 text-[var(--text-primary)]" strokeWidth={2} />
                    </div>
                    <h3 className="heading-3 mb-2">{value.title}</h3>
                    <p className="text-[var(--text-secondary)] leading-relaxed">
                      {value.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
